"use client"

import type React from "react"
import { createContext, useContext, useReducer, useState, useEffect } from "react"
import {
  type ProductDisplay,
  type DutchieCart,
  type DutchieCartItem,
  type CheckoutPricing,
  createDutchieCheckout,
} from "@/lib/api/dutchie"

interface CartContextType {
  cart: DutchieCart
  addToCart: (product: ProductDisplay, quantity: number) => void
  removeFromCart: (variantId: string) => void
  updateQuantity: (variantId: string, quantity: number) => void
  clearCart: () => void
  checkout: () => void
  isOpen: boolean
  openCart: () => void
  closeCart: () => void
  fulfillmentType: "pickup" | "delivery"
  setFulfillmentType: (type: "pickup" | "delivery") => void
  isLoading: boolean
  error: string | null
  clearError: () => void
  checkoutUrl: string | null
  isGeneratingUrl: boolean
  checkoutPricing: CheckoutPricing | null
}

// Cart expiration time (48 hours in milliseconds)
const CART_EXPIRATION_MS = 48 * 60 * 60 * 1000;

interface StoredCart extends DutchieCart {
  timestamp?: number;
}

// Cart action types
type CartAction = 
  | { type: 'ADD_TO_CART'; product: ProductDisplay; quantity: number }
  | { type: 'REMOVE_FROM_CART'; variantId: string }
  | { type: 'UPDATE_QUANTITY'; variantId: string; quantity: number }
  | { type: 'CLEAR_CART' }
  | { type: 'LOAD_CART'; cart: DutchieCart }

const initialCart: DutchieCart = {
  items: [],
  subtotal: 0,
  total: 0,
}

// Helper function to calculate totals
const calculateTotals = (items: DutchieCartItem[]): { subtotal: number; total: number } => {
  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  return {
    subtotal,
    total: subtotal, // Add tax calculation here if needed
  }
}

// Cart reducer function
const cartReducer = (state: DutchieCart, action: CartAction): DutchieCart => {
  switch (action.type) {
    case 'ADD_TO_CART': {
      const existingItemIndex = state.items.findIndex(
        (item) => item.variantId === action.product.variantId
      )

      let newItems: DutchieCartItem[]
      if (existingItemIndex >= 0) {
        // Update existing item
        newItems = [...state.items]
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + action.quantity,
        }
      } else {
        // Add new item
        newItems = [
          ...state.items,
          {
            productId: action.product.id,
            variantId: action.product.variantId,
            quantity: action.quantity,
            price: action.product.specialPrice || action.product.price,
            name: action.product.fullName,
            image: action.product.image,
            option: action.product.option,
          },
        ]
      }

      const { subtotal, total } = calculateTotals(newItems)
      return { items: newItems, subtotal, total }
    }

    case 'REMOVE_FROM_CART': {
      const newItems = state.items.filter((item) => item.variantId !== action.variantId)
      const { subtotal, total } = calculateTotals(newItems)
      return { items: newItems, subtotal, total }
    }

    case 'UPDATE_QUANTITY': {
      if (action.quantity <= 0) {
        // Remove item if quantity is 0 or less
        const newItems = state.items.filter((item) => item.variantId !== action.variantId)
        const { subtotal, total } = calculateTotals(newItems)
        return { items: newItems, subtotal, total }
      }

      const newItems = state.items.map((item) =>
        item.variantId === action.variantId ? { ...item, quantity: action.quantity } : item
      )
      const { subtotal, total } = calculateTotals(newItems)
      return { items: newItems, subtotal, total }
    }

    case 'CLEAR_CART':
      return initialCart

    case 'LOAD_CART':
      return action.cart

    default:
      return state
  }
}

const CartContext = createContext<CartContextType | undefined>(undefined)

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, dispatch] = useReducer(cartReducer, initialCart)
  const [isOpen, setIsOpen] = useState(false)
  const [fulfillmentType, setFulfillmentType] = useState<"pickup" | "delivery">("pickup")
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null)
  const [isGeneratingUrl, setIsGeneratingUrl] = useState(false)
  const [checkoutPricing, setCheckoutPricing] = useState<CheckoutPricing | null>(null)

  // Load cart and fulfillment type from localStorage on component mount
  useEffect(() => {
    const savedCart = localStorage.getItem("dutchieCart")
    if (savedCart) {
      try {
        const parsedCart: StoredCart = JSON.parse(savedCart)
        
        // Check if cart is expired (older than 48 hours)
        if (parsedCart.timestamp) {
          const age = Date.now() - parsedCart.timestamp;
          if (age > CART_EXPIRATION_MS) {
            console.log('🗑️ Cart expired (older than 48 hours), clearing...');
            localStorage.removeItem("dutchieCart");
            return;
          }
          
          // Log cart age for debugging
          const hoursOld = Math.floor(age / (60 * 60 * 1000));
          console.log(`📦 Loading cart (${hoursOld} hours old, ${parsedCart.items.length} items)`);
        }
        
        // Load the cart (validation will happen at checkout)
        dispatch({ type: 'LOAD_CART', cart: parsedCart })
      } catch (e) {
        console.error("Error parsing saved cart:", e)
        localStorage.removeItem("dutchieCart")
      }
    }
    
    const savedFulfillmentType = localStorage.getItem("dutchieFulfillmentType")
    if (savedFulfillmentType === "pickup" || savedFulfillmentType === "delivery") {
      setFulfillmentType(savedFulfillmentType)
    }
  }, [])

  // Save cart to localStorage whenever it changes (with timestamp)
  useEffect(() => {
    const cartWithTimestamp: StoredCart = {
      ...cart,
      timestamp: Date.now()
    };
    localStorage.setItem("dutchieCart", JSON.stringify(cartWithTimestamp))
  }, [cart])
  
  // Save fulfillment type to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("dutchieFulfillmentType", fulfillmentType)
  }, [fulfillmentType])

  // Pre-generate checkout URL whenever cart or fulfillment type changes
  useEffect(() => {
    const generateCheckoutUrl = async () => {
      // Only generate if cart has items
      if (cart.items.length === 0) {
        setCheckoutUrl(null)
        setCheckoutPricing(null)
        return
      }

      setIsGeneratingUrl(true)
      setError(null)
      
      try {
        console.log(`🔄 Pre-generating checkout URL for ${fulfillmentType} with ${cart.items.length} items`)
        const result = await createDutchieCheckout(cart, fulfillmentType)
        setCheckoutUrl(result.url)
        setCheckoutPricing(result.pricing)
        console.log(`✅ Checkout URL ready:`, result.url)
        if (result.pricing?.discounts && result.pricing.discounts > 0) {
          console.log(`💰 Bundle savings available: $${result.pricing.discounts.toFixed(2)}`)
        }
      } catch (error) {
        console.error('❌ Failed to pre-generate checkout URL:', error)
        setError('Failed to prepare checkout. Please try again.')
        setCheckoutUrl(null)
        setCheckoutPricing(null)
      } finally {
        setIsGeneratingUrl(false)
      }
    }

    // Debounce URL generation to avoid too many API calls
    const timeoutId = setTimeout(generateCheckoutUrl, 500)
    return () => clearTimeout(timeoutId)
  }, [cart.items, cart.subtotal, fulfillmentType])

  const addToCart = (product: ProductDisplay, quantity: number) => {
    dispatch({ type: 'ADD_TO_CART', product, quantity })
    // Don't auto-open cart - let users keep shopping
    // Cart count badge in nav shows items were added
  }

  const removeFromCart = (variantId: string) => {
    dispatch({ type: 'REMOVE_FROM_CART', variantId })
  }

  const updateQuantity = (variantId: string, quantity: number) => {
    dispatch({ type: 'UPDATE_QUANTITY', variantId, quantity })
  }

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' })
  }

  const clearError = () => {
    setError(null)
  }

  const checkout = async () => {
    if (cart.items.length === 0) {
      setError("Your cart is empty");
      return;
    }

    // Redirect to secure checkout page
    // This provides a clean iframe-based checkout experience
    window.location.href = '/checkout/secure';
  }

  const openCart = () => setIsOpen(true)
  const closeCart = () => setIsOpen(false)

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        checkout,
        isOpen,
        openCart,
        closeCart,
        fulfillmentType,
        setFulfillmentType,
        isLoading,
        error,
        clearError,
        checkoutUrl,
        isGeneratingUrl,
        checkoutPricing,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const context = useContext(CartContext)
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider")
  }
  return context
}
